import { useContext, useState } from "react";
import Layout from "../../Component/layout/main-layout";
import { useTranslation } from "react-i18next";
import layoutBgImg from "../../Images/layout/others.jpg";
import { Link, useNavigate } from "react-router-dom";
import GlobalLayout from "../../Component/layout/global-layout";
import { LoginUser } from "../../api/Queries/post";
import { Context } from "../../Store/context";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useContext(Context);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({});

  const change = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.username.trim()) {
      nextErrors.username = t("required");
    }
    if (!form.password) {
      nextErrors.password = t("required_password");
    }
    return nextErrors;
  };

  const getErrorMessage = (error) => {
    const data = error?.data || error?.response?.data;
    if (typeof data === "string") return data;
    if (data?.detail === "Неверные учетные данные") {
      return t("log_in_invalid");
    }
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;
    const firstKey =
      data && typeof data === "object" ? Object.keys(data)[0] : null;
    if (firstKey) {
      const value = data[firstKey];
      if (Array.isArray(value)) return String(value[0]);
      if (typeof value === "string") return value;
    }
    return error?.message || t("not_success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const payload = {
      username: form.username.trim(),
      password: form.password,
    };
    setIsLoading(true);
    try {
      const res = await LoginUser(payload);
      const token = res?.token || res?.access;
      if (token) {
        localStorage.setItem("token", token);
      }
      if (res?.refresh) {
        localStorage.setItem("refresh", res.refresh);
      }
      if (res?.user) {
        localStorage.setItem("user", JSON.stringify(res.user));
      }
      showSuccess(t("log_in_system"));
      navigate("/");
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlobalLayout
      title={`${t("log_in")} - ${t("supreme_court")} ${t("turkmenistan")}`}
      keywords=""
    >
      <div className="mb-20">
        <Layout title={t("log_in")} layoutBgImg={layoutBgImg} />

        <div className="flex justify-center w-full">
          <div
            className="bg-white xl:p-8 mt-8 xl:w-[40%]
                       dark:bg-dark-box w-[90%] p-4 rounded"
          >
            <h1
              className="text-center font-bold 
                         dark:text-white mb-1 text-3xl xl:text-4xl"
            >
              {t("log_in")}
            </h1>

            <p
              className="text-center text-text text-sm
                         dark:text-dark-text mt-2 mb-6"
            >
              {t("please")}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="relative mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("username") || "Username"}
                </label>

                <input
                  type="text"
                  name="username"
                  placeholder={t("username") || "Username"}
                  value={form.username}
                  onChange={(e) => change("username", e.target.value)}
                  autoComplete="username"
                  className={`w-full px-4 py-2 border dark:text-dark-text
                              bg-secondary mb-1 mt-1 rounded
                              focus:outline-none focus:ring-2 focus:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border
                              ${
                                errors.username
                                  ? "border-red"
                                  : "border-border"
                              }`}
                />

                {errors.username && (
                  <p className="text-red text-xs mb-2">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="relative mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("password")}
                </label>

                <div
                  className={`flex items-center border bg-secondary mb-1 mt-1 rounded
                              focus-within:ring-2 focus-within:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border
                              ${
                                errors.password
                                  ? "border-red"
                                  : "border-border"
                              }`}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="******"
                    value={form.password}
                    onChange={(e) => change("password", e.target.value)}
                    autoComplete="current-password"
                    className="w-full px-4 py-2 bg-transparent focus:outline-none dark:text-dark-text"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="px-3 text-sm text-primary dark:text-white"
                  >
                    {showPassword ? t("hide") : t("show")}
                  </button>
                </div>

                {errors.password && (
                  <p className="text-red text-xs mb-2">
                    {errors.password}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`relative text-white bg-primary px-6 
                            w-full mt-6 py-2 rounded min-h-[40px]
                            flex justify-center items-center
                            ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <span className={isLoading ? "invisible" : "visible"}>
                  {t("log_in")}
                </span>

                {isLoading && (
                  <span
                    className="absolute w-4 h-4 border-2 border-white 
                               border-t-transparent rounded-full animate-spin"
                  />
                )}
              </button>
            </form>

            <div className="mt-4 text-sm dark:text-dark-text">
              <span>{t("no_account")}</span>
              <Link
                to="/auth/registration"
                className="text-primary dark:text-white underline ml-1"
              >
                {t("registration")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </GlobalLayout>
  );
};

export default Login;